interface Plant {
  species: string
  genus: string
  family: string
  quantity: number
  spacing?: number  // in meters
  area?: number     // in square meters
}

/**
 * Represents a single plant species with its count and taxonomic classification.
 * Used for composite score calculations.
 */
interface SpeciesRecord {
  species: string
  genus: string
  family: string
  count: number
}

/**
 * Defines the weights for each component of the composite score.
 * The weights should sum to 1.
 */
interface ScoreWeights {
  santomour: number // e.g., 0.3
  giniSimpson: number // e.g., 0.5
  rankAbundance: number // e.g., 0.2
}

/**
 * Represents the detailed breakdown of the calculated scores.
 */
interface BiodiversityScores {
  santomour: {
    speciesPass: boolean
    genusPass: boolean
    familyPass: boolean
    score: number
  }
  giniSimpson: {
    index: number
  }
  rankAbundance: {
    slope: number
    score: number
  }
  compositeScore: number
}

interface DiversityResult {
  isValid: boolean
  speciesCount: number
  totalPlants: number
  genusDistribution: Record<string, number>
  familyDistribution: Record<string, number>
  simpsonsIndex: number
  compositeScores?: BiodiversityScores
  criteria: {
    minimumSpecies: {
      passed: boolean
      message: string
    }
    genusRule: {
      passed: boolean
      message: string
      violations: string[]
    }
    familyRule: {
      passed: boolean
      message: string
      violations: string[]
    }
    simpsonsRule: {
      passed: boolean
      message: string
      violations: string[]
    }
  }
}

export const useSpeciesDiversity = () => {
  // Helper to estimate area from spacing or vice versa
  const getPlantArea = (plant: Plant): number => {
    if (plant.area) return plant.area
    if (plant.spacing) {
      // Estimate area as a circle using spacing as diameter
      return Math.PI * Math.pow(plant.spacing / 2, 2)
    }
    // Default to 1 square meter if no spacing/area info
    return 1
  }

  const calculateSimpsonsIndex = (speciesCounts: Record<string, number>): number => {
    console.log('Calculating Simpson\'s Index with species counts:', speciesCounts)
    if (!speciesCounts || Object.keys(speciesCounts).length === 0) return 0
    
    // Try area-based calculation first
    const totalArea = Object.values(speciesCounts).reduce((sum, area) => sum + area, 0)
    const proportions = Object.values(speciesCounts).map(area => area / totalArea)
    const sumSquaredProportions = proportions.reduce((sum, p) => sum + (p * p), 0)
    const areaBasedIndex = 1 - sumSquaredProportions
    
    console.log('Area-based Simpson\'s Index:', {
      totalArea,
      proportions,
      sumSquaredProportions,
      areaBasedIndex
    })
    
    // Check if area-based index is valid (between 0 and 1)
    if (areaBasedIndex >= 0 && areaBasedIndex <= 1) {
      return areaBasedIndex
    }
    
    // Fallback to count-based calculation
    console.log('Falling back to count-based calculation')
    const N = Object.values(speciesCounts).reduce((sum, n) => sum + Math.round(n), 0)
    if (N <= 1) return 0
    
    const sumNumerator = Object.values(speciesCounts).reduce((sum, n) => {
      const count = Math.round(n)
      return sum + (count * (count - 1))
    }, 0)
    
    const countBasedIndex = 1 - (sumNumerator / (N * (N - 1)))
    
    console.log('Count-based Simpson\'s Index:', {
      N,
      sumNumerator,
      countBasedIndex
    })
    
    return countBasedIndex
  }

  /**
   * Calculates the score based on Santomour's Rule (10/20/30).
   */
  const calculateSantomourScore = (plantData: SpeciesRecord[], totalPlants: number) => {
    const speciesCounts: { [key: string]: number } = {}
    const genusCounts: { [key: string]: number } = {}
    const familyCounts: { [key: string]: number } = {}

    plantData.forEach(record => {
      speciesCounts[record.species] = (speciesCounts[record.species] || 0) + record.count
      genusCounts[record.genus] = (genusCounts[record.genus] || 0) + record.count
      familyCounts[record.family] = (familyCounts[record.family] || 0) + record.count
    })

    const maxSpeciesPercent = Math.max(...Object.values(speciesCounts)) / totalPlants
    const maxGenusPercent = Math.max(...Object.values(genusCounts)) / totalPlants
    const maxFamilyPercent = Math.max(...Object.values(familyCounts)) / totalPlants

    const speciesPass = maxSpeciesPercent <= 0.10
    const genusPass = maxGenusPercent <= 0.20
    const familyPass = maxFamilyPercent <= 0.30

    const score = (Number(speciesPass) + Number(genusPass) + Number(familyPass)) / 3

    return { speciesPass, genusPass, familyPass, score }
  }

  /**
   * Calculates the Gini-Simpson Index for composite score.
   */
  const calculateGiniSimpsonIndex = (plantData: SpeciesRecord[], totalPlants: number): number => {
    if (totalPlants === 0) return 0

    const sumOfSquaredProportions = plantData.reduce((sum, record) => {
      const proportion = record.count / totalPlants
      return sum + proportion * proportion
    }, 0)

    return 1 - sumOfSquaredProportions
  }

  /**
   * Calculates a score based on the slope of the rank-abundance curve.
   */
  const calculateRankAbundanceScore = (plantData: SpeciesRecord[], totalPlants: number) => {
    if (plantData.length < 2) {
      return { slope: 0, score: 0 } // No diversity if 0 or 1 species
    }

    const sortedData = [...plantData]
      .map(d => ({ ...d, abundance: d.count / totalPlants }))
      .sort((a, b) => b.abundance - a.abundance)

    // Use logarithmic abundance for slope calculation
    const points = sortedData.map((d, i) => ({
      x: i + 1, // Rank
      y: Math.log(d.abundance), // Log of relative abundance
    }))

    // Linear regression to find the slope
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0
    const n = points.length

    for (const p of points) {
      // Filter out -Infinity points if a species has 0 abundance
      if (isFinite(p.y)) {
        sumX += p.x
        sumY += p.y
        sumXY += p.x * p.y
        sumX2 += p.x * p.x
      }
    }

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX)

    // Normalize the slope to a 0-1 score
    // A slope of 0 is perfect evenness (score 1)
    // A very steep negative slope approaches 0
    const score = Math.exp(slope * 0.5)

    return { slope, score }
  }

  /**
   * Calculates the composite biodiversity score based on a list of species records.
   */
  const calculateCompositeBiodiversityScore = (
    plantData: SpeciesRecord[],
    weights: ScoreWeights = { santomour: 0.3, giniSimpson: 0.5, rankAbundance: 0.2 }
  ): BiodiversityScores => {
    if (plantData.length === 0) {
      return {
        santomour: { speciesPass: false, genusPass: false, familyPass: false, score: 0 },
        giniSimpson: { index: 0 },
        rankAbundance: { slope: 0, score: 0 },
        compositeScore: 0,
      }
    }

    const totalPlants = plantData.reduce((sum, record) => sum + record.count, 0)

    // 1. Santomour's Rule Calculation
    const santomourScores = calculateSantomourScore(plantData, totalPlants)

    // 2. Gini-Simpson Index Calculation
    const giniSimpsonIndex = calculateGiniSimpsonIndex(plantData, totalPlants)

    // 3. Rank-Abundance Curve Slope Calculation
    const rankAbundanceScores = calculateRankAbundanceScore(plantData, totalPlants)

    // 4. Calculate Composite Score
    const compositeScore =
      santomourScores.score * weights.santomour +
      giniSimpsonIndex * weights.giniSimpson +
      rankAbundanceScores.score * weights.rankAbundance

    return {
      santomour: {
        ...santomourScores,
      },
      giniSimpson: {
        index: giniSimpsonIndex,
      },
      rankAbundance: {
        ...rankAbundanceScores,
      },
      compositeScore: compositeScore,
    }
  }

  const evaluateDiversity = (plants: Plant[]): DiversityResult => {
    console.log('Evaluating diversity with plants:', plants)
    // Initialize counts
    const speciesCounts: Record<string, number> = {}
    const genusCounts: Record<string, number> = {}
    const familyCounts: Record<string, number> = {}
    
    // Count species, genera and families using area/spacing
    plants.forEach(plant => {
      const quantity = plant.quantity || 1
      // Calculate area coverage for this plant
      const area = getPlantArea(plant) * quantity
      
      // Add area to counts
      speciesCounts[plant.species] = (speciesCounts[plant.species] || 0) + area
      genusCounts[plant.genus] = (genusCounts[plant.genus] || 0) + area
      familyCounts[plant.family] = (familyCounts[plant.family] || 0) + area
    })

    console.log('Species area coverage:', speciesCounts)
    console.log('Genus area coverage:', genusCounts)
    console.log('Family area coverage:', familyCounts)

    const totalSpecies = Object.keys(speciesCounts).length
    const totalArea = Object.values(speciesCounts).reduce((sum, area) => sum + area, 0)
    const simpsonsIndex = calculateSimpsonsIndex(speciesCounts)

    // Convert Plant data to SpeciesRecord for composite score calculation
    const speciesRecords: SpeciesRecord[] = plants.map(plant => ({
      species: plant.species,
      genus: plant.genus,
      family: plant.family,
      count: plant.quantity || 1
    }))

    // Calculate composite biodiversity scores
    const compositeScores = calculateCompositeBiodiversityScore(speciesRecords)

    // Check violations using area proportions (Santamour's Rule: 10/20/30)
    const genusViolations = Object.entries(genusCounts)
      .filter(([_, area]) => (area / totalArea) > 0.2)
      .map(([genus]) => `${genus} exceeds 20% of total area`)

    const familyViolations = Object.entries(familyCounts)
      .filter(([_, area]) => (area / totalArea) > 0.3)
      .map(([family]) => `${family} exceeds 30% of total area`)

    const simpsonsViolations = []
    if (simpsonsIndex < 0.5) {
      simpsonsViolations.push("Simpson's Diversity Index is below 0.5, indicating low diversity")
    }

    return {
      isValid: totalSpecies >= 10 && genusViolations.length === 0 && 
               familyViolations.length === 0 && simpsonsIndex >= 0.5,
      speciesCount: totalSpecies,
      totalPlants: totalArea,
      genusDistribution: genusCounts,
      familyDistribution: familyCounts,
      simpsonsIndex,
      compositeScores,
      criteria: {
        minimumSpecies: {
          passed: totalSpecies >= 10,
          message: totalSpecies >= 10 ? 
            'Meets minimum species requirement' : 
            'Does not meet minimum of 10 species'
        },
        genusRule: {
          passed: genusViolations.length === 0,
          message: genusViolations.length === 0 ? 
            'No genus exceeds 20% of total area' : 
            'Some genera exceed 20% of total area',
          violations: genusViolations
        },
        familyRule: {
          passed: familyViolations.length === 0,
          message: familyViolations.length === 0 ? 
            'No family exceeds 30% of total area' : 
            'Some families exceed 30% of total area',
          violations: familyViolations
        },
        simpsonsRule: {
          passed: simpsonsIndex >= 0.5,
          message: simpsonsIndex >= 0.5 ?
            `Good diversity (Simpson's Index: ${simpsonsIndex.toFixed(2)})` :
            `Low diversity (Simpson's Index: ${simpsonsIndex.toFixed(2)})`,
          violations: simpsonsViolations
        }
      }
    }
  }

  return {
    evaluateDiversity,
    calculateSimpsonsIndex,
    calculateCompositeBiodiversityScore
  }
}
